import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { Ga4Property } from '@/api/types'
import { Card } from '@/design'

import { useIntegrationWebsites, websiteLabel } from '../useIntegrationWebsites'
import { Ga4Error } from './errors'
import { ownershipMismatchMessage } from './ownership'
import { useGa4Mutation } from './useGa4Mutation'

/** `123456789` or `properties/123456789`, as GA4's own UI shows either. */
const PROPERTY_ID = /^(?:properties\/)?([0-9]{1,20})$/

const streamsQueryKey = (propertyId: string) => ['ga4', 'property-streams', propertyId] as const

/** Step 2: name the property, and optionally the website it measures. */
export function ConnectForm({ email }: { email: string }) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const [propertyId, setPropertyId] = useState('')
  const [websiteId, setWebsiteId] = useState('')
  const [invalid, setInvalid] = useState(false)
  const [checkedId, setCheckedId] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const websites = useIntegrationWebsites()

  const connect = useGa4Mutation({
    mutationFn: (id: string): Promise<Ga4Property> => client.connectGa4({ propertyId: id, ...(websiteId ? { websiteId } : {}) }),
    onSuccess: () => {
      setPropertyId('')
      setCheckedId(null)
    },
  })

  /**
   * A read-only, best-effort check: advice, not the rule (the connect refusal stays
   * authoritative). Checked on blur and on submit, not on every keystroke.
   */
  const streamCheck = useQuery({
    queryKey: streamsQueryKey(checkedId ?? ''),
    queryFn: () => client.checkGa4Streams(checkedId as string),
    enabled: checkedId !== null,
    retry: false,
  })
  const notProvable = streamCheck.data?.ownershipProvable === false

  const checkNow = () => {
    const match = PROPERTY_ID.exec(propertyId.trim())
    setCheckedId(match?.[1] ?? null)
  }

  const onPropertyIdChange = (value: string) => {
    setPropertyId(value)
    const match = PROPERTY_ID.exec(value.trim())
    if ((match?.[1] ?? null) !== checkedId) setCheckedId(null)
  }

  /**
   * Submitting re-verifies rather than trusting `notProvable`: that flag reflects
   * whatever the last *blur* found, which is stale (or never ran) for a property
   * typed and submitted without one — e.g. paste then Enter. `fetchQuery` shares
   * the `streamCheck` query's cache and key, so this also drives the on-screen
   * mismatch message; a lookup failure here still lets Connect through, same as
   * a failed blur check — the connect refusal is the rule, this is advice.
   */
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const match = PROPERTY_ID.exec(propertyId.trim())
    setInvalid(match === null)
    if (!match?.[1]) return
    const id = match[1]
    setCheckedId(id)
    setChecking(true)
    let provable = true
    try {
      const check = await queryClient.fetchQuery({ queryKey: streamsQueryKey(id), queryFn: () => client.checkGa4Streams(id) })
      provable = check.ownershipProvable
    } catch {
      provable = true
    } finally {
      setChecking(false)
    }
    if (provable) connect.mutate(id)
  }

  return (
    <Card title="Step 2 — connect a property" subtitle="We check access, then import the last 90 days of traffic and keep it current daily.">
      <form aria-label="Connect a GA4 property" onSubmit={submit} className="ci-integration__form">
        <label className="ci-field">
          <span>GA4 property ID</span>
          <input
            inputMode="numeric"
            autoComplete="off"
            placeholder="123456789"
            value={propertyId}
            aria-invalid={invalid || undefined}
            onChange={(event) => onPropertyIdChange(event.target.value)}
            onBlur={checkNow}
          />
          <small className="ci-integration__id">
            The number from GA4 Admin › Property settings — not the "G-…" measurement ID.
          </small>
        </label>
        {invalid ? (
          <p role="alert" className="ci-integration__error">
            Enter the numeric property ID, for example 123456789.
          </p>
        ) : null}
        {!connect.error && notProvable && streamCheck.data ? (
          <p role="alert" className="ci-integration__error">
            {ownershipMismatchMessage(streamCheck.data.streamHosts, streamCheck.data.websiteHosts)}
          </p>
        ) : null}
        <label className="ci-field">
          <span>Website (optional)</span>
          <select value={websiteId} onChange={(event) => setWebsiteId(event.target.value)} disabled={websites.isPending}>
            <option value="">Not linked to a website</option>
            {(websites.data ?? []).map((website) => (
              <option key={website.id} value={website.id}>
                {websiteLabel(website)}
              </option>
            ))}
          </select>
        </label>
        <span className="ci-integration__actions">
          <button type="submit" className="ci-button" data-variant="primary" disabled={connect.isPending || checking || notProvable}>
            {connect.isPending ? 'Connecting…' : checking ? 'Checking…' : 'Connect property'}
          </button>
        </span>
        {connect.error ? (
          <Ga4Error error={connect.error} email={email} fallback="The property could not be connected." action="connect" />
        ) : !notProvable && streamCheck.isError ? (
          <Ga4Error
            error={streamCheck.error}
            email={email}
            fallback="This property could not be checked in advance. You can still try to connect it."
            action="connect"
          />
        ) : null}
        {connect.data ? (
          <p role="status" className="ci-integration__ok">
            Connected {connect.data.displayName}. Its traffic is being imported.
          </p>
        ) : null}
      </form>
    </Card>
  )
}
